import { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Plus, Search, Pencil, Trash2, X, CheckCircle2, CircleSlash2,
  Loader2, ArrowUpDown, FileText
} from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import MyContext from "../../../MyContext";
import "./CreditStyle.css";
import loader from "../../../images/Anlook.svg";

export default function CustomerUdhaariTable({ userID }) {
  const { baseUrl, config } = useContext(MyContext);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [sortKey, setSortKey] = useState("customerId");
  const [sortDir, setSortDir] = useState("asc");

  // ====== BILL MODAL STATE ======
  const [billOpen, setBillOpen] = useState(false);
  const [billRow, setBillRow] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [billLoading, setBillLoading] = useState(false);
  const [billUrl, setBillUrl] = useState("");

  // ---- Helpers
  const to2 = (v) => {
    const n = parseFloat(v ?? 0);
    return Number.isFinite(n) ? n.toFixed(2) : "0.00";
  };
  const mapApiToUi = (r) => ({
    customerId: String(r?.pk_customer_id ?? r?.customerId ?? ""),
    userid: String(r?.fk_user_id ?? r?.userid ?? ""),
    customerMobile: String(r?.customer_mobile ?? r?.customerMobile ?? ""),
    customerName: String(r?.customer_name ?? r?.customerName ?? ""),
    loyaltyPoints: String(r?.loyalty_points ?? r?.loyaltyPoints ?? "0"),
    creditLimit: to2(r?.credit ?? r?.creditLimit ?? 0),
    dueAmount: to2(r?.due_amount ?? 0),
    createdOn: String(r?.created_on ?? r?.createdOn ?? ""),
    creditAllowed: (r?.credit_allowed ?? r?.creditAllowed) === "Y" ? "Y" : "N",
  });

  // ---- APIs
  const addOrderCustomerAccountDetails = (payload) =>
    axios.post(`${baseUrl}/api/Order/v1.0/Credit/addOrderCustomerAccountDetails`, payload, config);

  const getAllOrderCustomerAccountDetails = (payload) =>
    axios.post(`${baseUrl}/api/Order/v1.0/Credit/getAllOrderCustomerAccountDetails`, payload, config);

  const updateOrderCustomerAccountDetails = (payload) =>
    axios.post(`${baseUrl}/api/Order/v1.0/Credit/updateOrderCustomerAccountDetails`, payload, config);

  const deleteOrderCustomerAccountDetails = (payload) =>
    axios.post(`${baseUrl}/api/Order/v1.0/Credit/deleteOrderCustomerAccountDetails`, payload, config);

  // NEW: generate image bill
  const generateCreditBill = ({ userid, mobileNumber, fromDate, toDate }) =>
    axios.post(`${baseUrl}/api/Order/v2.0/generateCreditBill`, { userid, mobileNumber, fromDate, toDate }, config);

  // NEW: send/settle customer bill
  const settleCreditBill = ({ userid, mobileNumber, fromDate, toDate, invoiceLink }) =>
    axios.post(`${baseUrl}/api/Order/v1.0/settleCreditBill`, { userid, mobileNumber, fromDate, toDate, invoiceLink }, config);

  // ---- Load
  async function reload() {
    if (!userID) { toast.error("Missing userid"); return; }
    try {
      setLoading(true);
      const res = await getAllOrderCustomerAccountDetails({ userid: userID });
      const ok = res?.data?.responseCode === "00";
      const list = ok ? res?.data?.response : res?.data || [];
      const mapped = Array.isArray(list) ? list.map(mapApiToUi) : [];
      setRows(mapped);
      if (!ok) toast.error(res?.data?.responseMessage || "Failed to fetch");
    } catch {
      toast.error("Failed to fetch customers");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [userID]);

  // ---- Filter + Sort
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? rows.filter(
          (r) =>
            String(r.customerMobile || "").toLowerCase().includes(q) ||
            String(r.customerId || "").toLowerCase().includes(q) ||
            String(r.customerName || "").toLowerCase().includes(q)
        )
      : rows.slice();

    base.sort((a, b) => {
      const A = String(a[sortKey] ?? "");
      const B = String(b[sortKey] ?? "");
      return sortDir === "asc" ? A.localeCompare(B, "en", { numeric: true }) : B.localeCompare(A, "en", { numeric: true });
    });
    return base;
  }, [rows, search, sortKey, sortDir]);

  function cycleSort(key) {
    setSortKey(key);
    setSortDir((d) => (d === "asc" ? "desc" : "asc"));
  }

  // ---- Drawer (unchanged)
  const [form, setForm] = useState({
    customerName: "",
    customerMobile: "",
    loyaltyPoints: "",
    creditAllowed: "N",
    creditLimit: "",
    remarks: "",
  });

  function openAdd() {
    setEditing(null);
    setForm({ customerName: "", customerMobile: "", loyaltyPoints: "", creditAllowed: "N", creditLimit: "", remarks: "" });
    setDrawerOpen(true);
  }
  function openEdit(row) {
    setEditing(row);
    setForm({
      customerName: row?.customerName || "",
      customerMobile: row?.customerMobile || "",
      loyaltyPoints: row?.loyaltyPoints ?? "",
      creditAllowed: row?.creditAllowed === "Y" ? "Y" : "N",
      creditLimit: row?.creditLimit ?? "",
      remarks: "",
    });
    setDrawerOpen(true);
  }
  function closeDrawer() {
    if (!submitting) {
      setDrawerOpen(false);
      setEditing(null);
    }
  }
  function validate() {
    if (!editing) {
      if (!(form.customerName || "").trim()) return "Enter customer name";
      const mobile = (form.customerMobile || "").trim();
      if (!/^[6-9]\d{9}$/.test(mobile)) return "Enter valid 10-digit Indian mobile";
    }
    if (form.loyaltyPoints !== "" && !/^\d+(\.\d+)?$/.test(form.loyaltyPoints)) return "Loyalty Points must be a number";
    if (form.creditLimit !== "" && !/^\d+(\.\d+)?$/.test(form.creditLimit)) return "Credit Limit must be a number";
    if (!["Y", "N"].includes(form.creditAllowed)) return "Credit Allowed must be Y or N";
    return null;
  }
  async function handleSubmit(e) {
    e?.preventDefault?.();
    const err = validate(); if (err) return toast.warning(err);
    try {
      setSubmitting(true);
      if (editing) {
        const res = await updateOrderCustomerAccountDetails({
          customerId: editing.customerId,
          loyaltyPoints: String(form.loyaltyPoints ?? ""),
          creditAllowed: form.creditAllowed,
          creditLimit: String(form.creditLimit ?? ""),
        });
        if (res?.data?.responseCode === "00") { toast.success(res?.data?.responseMessage || "Customer updated"); closeDrawer(); reload(); }
        else toast.error(res?.data?.responseMessage || "Update failed");
      } else {
        const res = await addOrderCustomerAccountDetails({
          userid: userID,
          customerName: String(form.customerName ?? ""),
          customerMobile: String(form.customerMobile ?? ""),
          loyaltyPoints: String(form.loyaltyPoints ?? ""),
          creditAllowed: form.creditAllowed,
          creditLimit: String(form.creditLimit ?? ""),
          remarks: String(form.remarks ?? ""),
        });
        if (res?.data?.responseCode === "00") { toast.success(res?.data?.responseMessage || "Customer added"); closeDrawer(); reload(); }
        else toast.error(res?.data?.responseMessage || "Add failed");
      }
    } catch {
      toast.error("Operation failed");
    } finally {
      setSubmitting(false);
    }
  }

  // ---- Toggle Credit
  async function toggleCreditAllowed(row) {
    const next = row?.creditAllowed === "Y" ? "N" : "Y";
    const old = row?.creditAllowed;
    setRows((prev) => prev.map((it) => (it.customerId === row.customerId ? { ...it, creditAllowed: next } : it)));
    try {
      const res = await updateOrderCustomerAccountDetails({
        customerId: row.customerId,
        loyaltyPoints: String(row.loyaltyPoints ?? ""),
        creditAllowed: next,
        creditLimit: String(row.creditLimit ?? ""),
      });
      if (res?.data?.responseCode === "00") toast.success(`Credit ${next === "Y" ? "enabled" : "disabled"}`);
      else throw new Error(res?.data?.responseMessage || "Toggle failed");
    } catch {
      toast.error("Toggle failed");
      setRows((prev) => prev.map((it) => (it.customerId === row.customerId ? { ...it, creditAllowed: old } : it)));
    }
  }

  // ---- Delete
  async function handleDelete() {
    if (!deleting?.customerId) return;
    try {
      setSubmitting(true);
      const res = await deleteOrderCustomerAccountDetails({ customerId: deleting.customerId });
      if (res?.data?.responseCode === "00") { toast.success(res?.data?.responseMessage || "Customer deleted"); setDeleting(null); reload(); }
      else toast.error(res?.data?.responseMessage || "Delete failed");
    } catch {
      toast.error("Delete failed");
    } finally {
      setSubmitting(false);
    }
  }

  // ====== BILL MODAL handlers ======
  function openBill(row) {
    setBillRow(row);
    setFromDate(today);
    setToDate(today);
    setBillUrl("");
    setBillOpen(true);
  }
  function closeBill() {
    if (!billLoading) setBillOpen(false);
  }

  async function onFetchBill() {
    if (!userID || !billRow?.customerMobile) {
      return toast.error("Missing userid or mobile");
    }
    try {
      setBillLoading(true);
      const payload = {
        userid: String(userID),
        mobileNumber: String(billRow.customerMobile),
        fromDate: String(fromDate),
        toDate: String(toDate),
      };
      const res = await generateCreditBill(payload);
      // Backend returns { image_url: "..." }
      const url = res?.data?.image_url || "https://dummyimage.com/1080x1528/eeeeee/555555.png&text=Sample+Credit+Bill";
      const finalUrl = url.includes("?") ? `${url}&_=${Date.now()}` : `${url}?_=${Date.now()}`;
      setBillUrl(finalUrl);
      toast.success("Bill generated");
    } catch {
      setBillUrl("https://dummyimage.com/1080x1528/eeeeee/555555.png&text=Sample+Credit+Bill");
      toast.warn("Showing sample bill preview");
    } finally {
      setBillLoading(false);
    }
  }

  async function onSendToCustomer() {
    if (!userID || !billRow?.customerMobile) {
      return toast.error("Missing userid or mobile");
    }
    if (!billUrl) {
      return toast.warning("Please fetch the bill first");
    }
    try {
      setBillLoading(true);
      const payload = {
        userid: String(userID),
        mobileNumber: String(billRow.customerMobile),
        fromDate: String(fromDate),
        toDate: String(toDate),
        invoiceLink: String(billUrl),
      };
      const res = await settleCreditBill(payload);
      if (res?.data?.responseCode === "00") toast.success(res?.data?.responseMessage || "Bill sent to customer");
      else toast.error(res?.data?.responseMessage || "Failed to send bill");
    } catch {
      toast.error("Failed to send bill");
    } finally {
      setBillLoading(false);
    }
  }

  return (
    <div className="admin-credit-block">
           <ToastContainer position="top-right" theme="colored" closeOnClick pauseOnHover={false} />
      {/* Toolbar */}
      <div className="admin-credit-toolbar">
        <div className="admin-input with-icon">
          <Search size={16} className="admin-input-icon" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, mobile or ID..." />
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button onClick={openAdd} className="admin-btn admin-btn-success"><Plus size={18} /> Add Customer</button>
          <span className="admin-badge">Total: {rows.length}</span>
          {loading && <span className="admin-badge muted">Loading…</span>}
        </div>
      </div>

      {/* Table */}
      <div className="admin-table-wrap">
        {loading ? (
          <div className=" m-5 p-5 d-flex justify-content-center">
            <div className=" d-flex flex-column align-items-center justify-content-center ">
              <div className="p-3"><div className="loader"> </div></div>
              <div className="p-3"><img src={loader} alt="" height={40} /></div>
            </div>
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th onClick={() => cycleSort("customerId")}><ArrowUpDown size={14}/> ID</th>
                <th onClick={() => cycleSort("customerMobile")}><ArrowUpDown size={14}/> Mobile</th>
                <th>Name</th>
                <th>Credit Limit</th>
                <th>Due Amount</th>
                <th>Created on</th>
                <th>Status</th>
                <th style={{ width: 210 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={9} className="center muted">No customers found</td></tr>
              ) : filtered.map((r) => {
                const creditOn = r.creditAllowed === "Y";
                return (
                  <tr key={r.customerId || r.customerMobile}>
                    <td className="mono">{r.customerId || "—"}</td>
                    <td className="mono">{r.customerMobile || "—"}</td>
                    <td>{r.customerName || "—"}</td>
                    <td>₹ {to2(r.creditLimit)}</td>
                    <td>₹ {to2(r.dueAmount)}</td>
                    <td>{r.createdOn || "—"}</td>
                    <td>
                      <span className={`admin-chip ${creditOn ? "yes" : "no"}`}>
                        {creditOn ? <CheckCircle2 size={14}/> : <CircleSlash2 size={14}/>}
                        {creditOn ? "Credit ON" : "Credit OFF"}
                      </span>
                    </td>
                    <td className="row-actions" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button className="admin-icon-btn" title="Toggle Credit" onClick={() => toggleCreditAllowed(r)}>
                        {creditOn ? <CheckCircle2 size={18}/> : <CircleSlash2 size={18}/>}
                      </button>
                      <button className="admin-icon-btn" title="Edit" onClick={() => openEdit(r)}>
                        <Pencil size={18}/>
                      </button>
                      {/* BILL ACTION */}
                      <button className="admin-btn admin-btn-neutral btn--sm" onClick={() => openBill(r)}>
                        <FileText size={16} style={{ marginRight: 6 }}/> Bill
                      </button>
                      {/* <button className="admin-icon-btn danger" title="Delete" onClick={() => setDeleting(r)}>
                        <Trash2 size={18}/>
                      </button> */}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Drawer */}
      {drawerOpen && (
        <div className="admin-drawer-backdrop" onClick={closeDrawer}>
          <div className="admin-drawer admin-drawer-compact" onClick={(e) => e.stopPropagation()}>
            <div className="admin-drawer-header">
              <h4 className="admin-drawer-title">{editing ? "Edit Customer" : "Add Customer"}</h4>
              <button className="admin-icon-btn" onClick={closeDrawer}><X size={18} /></button>
            </div>

            <form className="admin-drawer-body admin-drawer-form" onSubmit={handleSubmit}>
              {!editing && (
                <>
                  <div className="admin-field">
                    <label>Customer Name <span className="req">*</span></label>
                    <input
                      className="admin-input-control"
                      placeholder="Full name"
                      value={form.customerName}
                      onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))}
                    />
                  </div>
                  <div className="admin-field">
                    <label>Remark</label>
                    <input
                      className="admin-input-control"
                      placeholder="Add Remark"
                      value={form.remarks}
                      onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))}
                    />
                  </div>
                  <div className="admin-field">
                    <label>Customer Mobile <span className="req">*</span></label>
                    <input
                      className="admin-input-control"
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="10-digit mobile"
                      value={form.customerMobile}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, customerMobile: e.target.value.replace(/\D/g, "").slice(0, 10) }))
                      }
                    />
                  </div>
                </>
              )}

              <div className="admin-field">
                <label>Loyalty Points</label>
                <input
                  className="admin-input-control"
                  inputMode="decimal"
                  placeholder="e.g., 120"
                  value={form.loyaltyPoints}
                  onChange={(e) => setForm((f) => ({ ...f, loyaltyPoints: e.target.value }))}
                />
              </div>

              <div className="admin-field">
                <label>Credit Allowed</label>
                <div className="admin-toggle admin-toggle-pill">
                  <button type="button" className={`admin-toggle-btn ${form.creditAllowed === "Y" ? "active" : ""}`} onClick={() => setForm((f) => ({ ...f, creditAllowed: "Y" }))}>Yes</button>
                  <button type="button" className={`admin-toggle-btn ${form.creditAllowed === "N" ? "active" : ""}`} onClick={() => setForm((f) => ({ ...f, creditAllowed: "N" }))}>No</button>
                </div>
              </div>

              <div className="admin-field">
                <label>Credit Limit (₹)</label>
                <input
                  className="admin-input-control"
                  inputMode="decimal"
                  placeholder="e.g., 5000"
                  value={form.creditLimit}
                  onChange={(e) => setForm((f) => ({ ...f, creditLimit: e.target.value }))}
                />
              </div>

              <div className="admin-drawer-footer">
                <button type="button" className="admin-btn" onClick={closeDrawer}>Cancel</button>
                <button type="submit" className="admin-btn admin-btn-success" disabled={submitting}>
                  {submitting ? <Loader2 className="admin-spin" size={16} /> : null}
                  {editing ? "Update" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleting && (
        <div className="admin-modal-backdrop" onClick={() => setDeleting(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h4>Delete Customer</h4>
              <button className="admin-icon-btn" onClick={() => setDeleting(null)}><X size={18} /></button>
            </div>
            <div className="admin-confirm">
              <p>Delete <b>{deleting.customerMobile}</b>? This cannot be undone.</p>
              <div className="admin-modal-actions">
                <button className="admin-btn" onClick={() => setDeleting(null)}>Cancel</button>
                <button className="admin-btn admin-btn-danger" disabled={submitting} onClick={handleDelete}>
                  {submitting ? <Loader2 className="admin-spin" size={16} /> : null}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ====== Generate Credit Bill Modal (compact + responsive) ====== */}
      {billOpen && (
        <div className="admin-modal-backdrop" onClick={closeBill}>
          <div
            className="admin-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "95%",
              maxWidth: 820,          // narrower to prevent horizontal stretch
              display: "flex",
              flexDirection: "column",
              maxHeight: "88vh",
            }}
          >
            <div className="admin-modal-header">
              <h4>Generate Credit Bill</h4>
              <button className="admin-icon-btn" onClick={closeBill}><X size={18} /></button>
            </div>

            {/* Scrollable body */}
            <div className="admin-drawer-body admin-drawer-form" style={{ overflowY: "auto" }}>
              {/* Top form grid */}
              <div
                className="admin-field"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 12,
                }}
              >
                <div>
                  <label>Mobile</label>
                  <div className="admin-input-control mono" style={{ opacity: 0.9 }}>
                    {billRow?.customerMobile || "—"}
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 12 }}>
                  <div>
                    <label>From Date</label>
                    <input type="date" className="admin-input-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                  </div>
                  <div>
                    <label>To Date</label>
                    <input type="date" className="admin-input-control" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                  </div>
                  <div style={{ alignSelf: "end" }}>
                    <button className="admin-btn admin-btn-primary" onClick={onFetchBill} disabled={billLoading}>
                      {billLoading ? <Loader2 className="admin-spin" size={16}/> : <FileText size={16}/>}
                      <span style={{ marginLeft: 6 }}>{billLoading ? "Fetching…" : "Fetch Bill"}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Preview */}
              {billUrl && (
                <div className="admin-field">
                  <label>Preview</label>
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: 8,
                      border: "1px solid #eee",
                      borderRadius: 8,
                      background: "#fafafa",
                    }}
                  >
                    <img
                      src={billUrl}
                      alt="Credit Bill"
                      style={{ width: "100%", maxWidth: 560, height: "auto", objectFit: "contain", borderRadius: 6 }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="admin-modal-actions" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <button className="admin-btn" onClick={closeBill} disabled={billLoading}>Close</button>
              <button
                className="admin-btn admin-btn-success"
                onClick={onSendToCustomer}
                disabled={billLoading || !billRow?.customerMobile || !billUrl}
                title={!billUrl ? "Fetch bill first" : "Send bill to customer"}
              >
                {billLoading ? <Loader2 className="admin-spin" size={16}/> : null}
                Send to customer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
